import useIsMounted from '@github-ui/use-is-mounted'
import type {Icon} from '@primer/octicons-react'
import {CheckIcon, CopyIcon} from '@primer/octicons-react'
import type {SxProp, TooltipProps} from '@primer/react'
import {Box, IconButton, Tooltip} from '@primer/react'
import React from 'react'

import {copyText} from './copy'

const copyConfirmationMsDelay = 2000
const padding = 1

export interface CopyToClipboardButtonProps extends SxProp {
  /**
   * Component that will be shown for a few seconds after the user clicks copy.
   * By default, tooltip props and sx styles will be passed to the CopyConfirmationCheck default component.
   *
   * @default CopyConfirmationCheck
   */
  confirmationComponent?: React.ReactNode
  /**
   * Octocon that is displayed on the copy button
   *
   * @default CopyIcon
   */
  icon?: Icon
  /**
   * Size of the button, passed to IconButton
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Optional callback that is invoked when the user clicks the copy button
   */
  onCopy?: () => void
  /**
   * Text that will be copied to the clipboard
   */
  textToCopy: string
  /**
   * Props that will be applied to tooltips
   */
  tooltipProps?: TooltipProps
  /**
   * Text that will be displayed in the tooltip
   */
  ariaLabel?: string | null
  /**
   * If the button should be accessible or not
   */
  accessibleButton?: boolean
}

function CopyConfirmationCheck({sx, tooltipProps}: {tooltipProps?: TooltipProps} & SxProp) {
  return (
    <Tooltip aria-label="Copied!" sx={sx} {...tooltipProps}>
      <Box as="span" sx={{display: 'inline-block', color: 'success.fg', p: padding, mr: 1}}>
        <CheckIcon />
      </Box>
    </Tooltip>
  )
}

export function CopyToClipboardButton({
  icon = CopyIcon,
  size = 'medium',
  onCopy,
  sx,
  textToCopy,
  tooltipProps,
  confirmationComponent = <CopyConfirmationCheck sx={sx} tooltipProps={tooltipProps} />,
  ariaLabel,
  accessibleButton,
}: CopyToClipboardButtonProps) {
  const [copied, setCopied] = React.useState(false)
  const isMounted = useIsMounted()
  const onClickCopy = () => {
    setCopied(true)
    void copyText(textToCopy)
    onCopy?.()
    setTimeout(() => isMounted() && setCopied(false), copyConfirmationMsDelay)
  }

  const label = ariaLabel ?? `Copy ${textToCopy} to clipboard`
  return copied ? (
    <>{confirmationComponent}</>
  ) : (
    <Tooltip aria-label={label} {...tooltipProps}>
      <IconButton
        aria-label={label}
        icon={icon}
        variant="invisible"
        size={size}
        tabIndex={accessibleButton === false ? -1 : 0}
        sx={{
          ...sx,
        }}
        onClick={onClickCopy}
      />
    </Tooltip>
  )
}

try{ CopyConfirmationCheck.displayName ||= 'CopyConfirmationCheck' } catch {}
try{ CopyToClipboardButton.displayName ||= 'CopyToClipboardButton' } catch {}